import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import { chartOfAccountsSelectors } from 'app-components/flux/chart-of-accounts';
import type { tagsTypes } from 'app-components/flux/tags';
import { List } from 'immutable';

import PayeeAutocomplete from 'components/PayeeAutocomplete';
import QAmountField from 'components/QAmountField';
import MuiTagsField from 'components/QuickenControls/TagsField/TagsField';
import { useAppSelector } from 'hooks/redux';

import type { InvestmentTransactionFormikProps } from './types';

type TagValue = tagsTypes.Tag | tagsTypes.TagProps;
type PayeeAutocompleteProps = {
  inputValue?: string | null;
  onInputChange?: (event: React.SyntheticEvent, value: string) => void;
  textFieldProps?: {
    error?: boolean;
    helperText?: string;
    style?: React.CSSProperties;
    autoFocus?: boolean;
  };
  selectOnFocus?: boolean;
  openOnFocus?: boolean;
};

const TypedPayeeAutocomplete = PayeeAutocomplete as React.ComponentType<PayeeAutocompleteProps>;

const CashFlowTransactionForm = (props: InvestmentTransactionFormikProps) => {
  const { formik } = props;

  const isIncome = useAppSelector((state) => chartOfAccountsSelectors.isIncome(state, formik.values.coa));
  const [autoSign, setAutoSign] = useState(formik.values.amount > 0 === isIncome);

  useEffect(() => {
    formik.registerField('amount', {
      validate: (value: number) => (typeof value === 'number' && Number(value) !== 0 ? undefined : 'use valid number'),
    });
    formik.registerField('payee', { validate: (value: string | null | undefined) => (value ? undefined : 'required') });
    return () => {
      formik.unregisterField('amount');
      formik.unregisterField('payee');
    };
  }, [formik]);

  useEffect(() => {
    // only update strict field sign on income category change
    if (autoSign) {
      const amount = (isIncome ? +1 : -1) * Math.abs(formik.values.amount);
      if (!Number.isNaN(amount) && amount !== formik.values.amount) {
        formik.setFieldValue('amount', amount);
      }
    }
  }, [isIncome]); // eslint-disable-line react-hooks/exhaustive-deps

  const { tags } = formik.values;
  const tagsValue = Array.isArray(tags) ? tags : tags?.toArray() || [];

  return (
    <>
      <Box display='flex' gap={2}>
        <TypedPayeeAutocomplete
          inputValue={formik.values.payee}
          onInputChange={(_event: React.SyntheticEvent, value: string) => formik.setFieldValue('payee', value)}
          textFieldProps={{
            error: Boolean(formik.submitCount > 0 && formik.errors.payee),
            helperText: (formik.submitCount > 0 && formik.errors.payee) || '',
            style: { width: 240 },
            autoFocus: true,
          }}
          selectOnFocus
          openOnFocus={false}
        />
        <MuiTagsField
          id='tags'
          value={tagsValue}
          onChange={(_event: React.SyntheticEvent, tagArray: TagValue[]) =>
            formik.setFieldValue('tags', List(tagArray))
          }
        />
      </Box>

      <QAmountField
        id='amount'
        editable
        label='Total amount'
        sx={{ minWidth: 110 }}
        expectedSign={isIncome ? 'positive' : 'negative'}
        value={formik.values.amount}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const value = Number(event.target.value);
          if (!Number.isNaN(value)) {
            setAutoSign(Math.sign(value) === (isIncome ? +1 : -1));
            formik.setFieldValue('amount', value);
          }
        }}
        error={Boolean(formik.submitCount > 0 && formik.errors.amount)}
        helperText={(formik.submitCount > 0 && formik.errors.amount) || ''}
      />
    </>
  );
};

export default CashFlowTransactionForm;
